/**
 * 「浏览与编辑」两个字/词列表的**总数**（分页已按用户要求删除）。
 *
 * ## ⚠️ 2026-10-20：这里原来叫「增量渲染」，现在**没有分页了**
 *
 * 上一版按用户 2026-10-19 的话做过 ②：首屏 100 + 每次「再显示 100 个」。
 * 2026-10-20 用户改主意了，原话（验收标准）：
 *
 * > "查询出那些字的列表之后，不是每 100 个显示一批嘛，我觉得老是去点那 100 个
 * >  也挺麻烦的，你就**一次性全部显示**吧，有多少个就显示多少个，**取消那个 100 的限制**。"
 *
 * 所以：
 *   · 「再显示 100 个 / 再显示剩下的 N 个」按钮 **已删**；
 *   · 「已显示 100 / 共 280 个」那行 **已删**；
 *   · **只保留一个总数**：「共 280 个」—— 就是本文件算的那一句；
 *   · 组件不再有 `hanziShown` / `wordShown` / `revealIndex`，所有条目一次渲染。
 *
 * ## 为什么文件名还是 `dictationBrowsePaging.ts`
 *
 * 刻意**不删文件**（本仓不允许为了改名而删文件），文件名只是历史。内容已经
 * 与"分页"无关：它只回答两件事 —— **一共多少个**、**这次有没有取全**。
 *
 * ## 为什么是纯函数
 *
 * 本仓前端测试**不引 jsdom / testing-library**（不加依赖，见 `vitest.config.ts`），
 * 所以"总数为空 / 取到的比总数少"这两种情况必须能在不渲染组件的情况下钉住。
 * 文案也只有这一份，界面与测试不会各写一遍。
 */

/** 这一屏"总共多少条"的展示口径（`describeBrowseTotal` 的输出）。 */
export interface BrowseTotalView {
  /** 一共多少条（后端 `total`；拿不到时退回"这次取到多少条"）。 */
  total: number;
  /** 这次**真的取到**多少条（后端上限 / 数据缺失时可能小于 `total`）。 */
  available: number;
  /** 全部取到了没有。 */
  allShown: boolean;
  /** 唯一保留的总数文案：`共 280 个`。 */
  label: string;
  /**
   * 服务端这次只返回了一部分时的说明（`null` = 不该说）。
   *
   * ⚠️ 这是"取不全"的**诚实提示**：不能假装已经全部显示。上限见
   *    `DICTATION_BROWSE_LIMIT_MAX`（20000）；正常部署（一册 280、全站几百到几千）
   *    不会出现它。
   */
  truncatedLabel: string | null;
}

/**
 * 字/词列表底部的总数。
 *
 * @param total     后端说一共多少条（`null` = 没拿到 → 当成"取到多少就是多少"）
 * @param available 这次请求真的取回多少条（也就是**渲染出来**多少条，不再分页）
 */
export function describeBrowseTotal(
  total: number | null | undefined,
  available: number,
): BrowseTotalView {
  const realAvailable = Math.max(0, Math.trunc(available));
  /**
   * ⚠️ 夹到 `>= available`：后端说"共 0 条"却返回了 N 条是**数据自相矛盾**，
   *    这时按"至少取到了 N 条"记，不能显示「共 0 个」却还画着 N 张卡。
   *    正常情况（`total >= available`）一个字都不变。
   */
  const realTotal =
    total === null || total === undefined
      ? realAvailable
      : Math.max(0, Math.trunc(total), realAvailable);

  return {
    total: realTotal,
    available: realAvailable,
    allShown: realTotal <= realAvailable,
    label: `共 ${realTotal} 个`,
    truncatedLabel:
      realTotal > realAvailable
        ? `服务器这次只返回了 ${realAvailable} 条（共 ${realTotal} 条），缩小筛选范围才能看全。`
        : null,
  };
}
